import React, { useEffect, useState, useRef } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Line,
    ComposedChart,
    LabelList,
} from "recharts";
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

import './Graficos.css';

import iPosto from '../interfaces/iPosto';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const cores = [
    '#0B3D91', '#1E90FF', '#00BFFF', '#4682B4', '#5F9EA0',
    '#6495ED', '#4169E1', '#0000CD', '#00688B', '#008B8B',
    '#20B2AA', '#48D1CC', '#40E0D0', '#00CED1', '#00FA9A',
    '#00FF7F', '#32CD32', '#2E8B57', '#3CB371', '#66CDAA',
    '#8FBC8F', '#228B22', '#008000', '#006400', '#2F4F4F',
    '#556B2F', '#6B8E23', '#7FFF00', '#7CFC00', '#ADFF2F'
];
const useElementWidth = () => {
    const [elementWidth, setElementWidth] = useState(0);

    useEffect(() => {
        const element = document.getElementById('graficontainer');

        const handleResize = () => {
            if (element) {
                setElementWidth(element.offsetWidth);
            }
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return elementWidth;
};

const Graficos = ({ setCurrentPage }: { setCurrentPage: Function; }) => {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [postosList, setPostosList] = useState<iPosto[]>([]);

    const [dataG1, setDataG1] = useState<any[]>([]);
    const [dataG2, setDataG2] = useState<any[]>([]);
    const [dataG3, setDataG3] = useState<any[]>([]);

    const [searchInput, setSearchInput] = useState({
        g1_ano: getAnoAtual(),
        g2_posto: '',
        g2_mes: "02",
        g2_ano: getAnoAtual(),
        g3_posto: '',
        g3_ano: getAnoAtual(),
    });

    const [anoComparativoMes, setAnoComparativoMes] = useState(getAnoAtual());
    const [mesComparativoMes, setMesComparativoMes] = useState("01");
    const [dataComparativoMes, setDataComparativoMes] = useState<any[]>([]);
    const [tabelaComparativoMes, setTabelaComparativoMes] = useState<any[]>([]);

    const [labelG2, setLabelG2] = useState('');
    const [labelG3, setLabelG3] = useState('');
    const [labelComparativoMes, setLabelComparativoMes] = useState('');
    const [labelG1, setLabelG1] = useState('');

    function getAnoAtual(): number {
        return new Date().getFullYear();
    }

    const { bootStrapColor } = useSelector((state: RootState) => state.config);

    const { g1_ano, g2_posto, g2_mes, g2_ano, g3_posto, g3_ano } = searchInput;

    const handleSearchInputChanges = (element: any) => {
        setSearchInput({ ...searchInput, [element.target.name]: element.target.value });
    };

    const getNomePostoById = (num: string) => {
        const item = postosList.find((element: iPosto) => element.posto_id.toString() === num.toString());
        return item ? item.posto_nome : null;
    };

    const submitSearch1 = async () => {
        try {
            const response = await fetch(`${apiUrl}/grafico/1/${searchInput.g1_ano}`);
            const jsonResponse = await response.json();
            setDataG1(jsonResponse);
            setLabelG1(`Ano ${searchInput.g1_ano}`);
        } catch (err: any) {
            console.error(err.message);
            setLabelG1('');
        }
    };
    const submitSearch2 = async (init?: iPosto) => {
        let posto = getNomePostoById(searchInput.g2_posto);
        let mes = searchInput.g2_mes;
        let ano = searchInput.g2_ano;
        if (init) {
            posto = init.posto_nome;
        }
        try {
            const response = await fetch(`${apiUrl}/grafico/2/${posto}/${mes}/${ano}`);
            const jsonResponse = await response.json();
            setDataG2(jsonResponse);
            setLabelG2(`${posto} - ${mes}/${ano}`);
        } catch (err: any) {
            console.error(err.message);
            setLabelG2('');
        }
    };
    const submitSearch3 = async (init?: iPosto) => {
        let posto = getNomePostoById(searchInput.g3_posto);
        let ano = searchInput.g3_ano;
        if (init) {
            posto = init.posto_nome;
        }
        if (!posto) return;
        try {
            const response = await fetch(`${apiUrl}/grafico/3/${posto}/${ano}`);
            const jsonResponse = await response.json();
            setDataG3(jsonResponse);
            setLabelG3(`${posto} - ${ano}`);
        } catch (err: any) {
            console.error(err.message);
            setLabelG3('');
        }
    };
    // Ref para o card "Média histórica"
    const mediaHistoricaRef = useRef<HTMLDivElement>(null);

    const postoClicked = async (posto: iPosto) => {
        setSearchInput((prev) => ({
            ...prev,
            g2_posto: posto.posto_id,
            g3_posto: posto.posto_id,
        }));

        await submitSearch2(posto);
        await submitSearch3(posto);

        // Scroll suave até o gráfico "Média histórica"
        setTimeout(() => {
            mediaHistoricaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const buscarTabelaComparativoMes = async () => {
        try {
            const response = await fetch(`${apiUrl}/consulta-registros?ano=${anoComparativoMes}&mes=${Number(mesComparativoMes)}`);
            const json = await response.json();
            setTabelaComparativoMes(json);
        } catch (err) {
            setTabelaComparativoMes([]);
        }
    };

    const buscarComparativoMes = async () => {
        try {
            const response = await fetch(`${apiUrl}/grafico/comparativo-mes/${anoComparativoMes}/${mesComparativoMes}`);
            const json = await response.json();
            setDataComparativoMes(json);
            setLabelComparativoMes(`${mesComparativoMes}/${anoComparativoMes}`);
            await buscarTabelaComparativoMes();
        } catch (err) {
            setLabelComparativoMes('');
            setTabelaComparativoMes([]);
        }
    };

    const handleInit = async () => {
        try {
            const response = await fetch(`${apiUrl}/all-postos`);
            const jsonPostosList: iPosto[] = await response.json();

            if (!jsonPostosList || jsonPostosList.length === 0) {
                console.warn("Lista de postos vazia ou inválida");
                return;
            }

            setPostosList(jsonPostosList);

            const primeiroPostoId = jsonPostosList[0].posto_id?.toString();

            setSearchInput((prev) => ({
                ...prev,
                g2_posto: primeiroPostoId,
                g3_posto: primeiroPostoId,
            }));

            await submitSearch1();
            await submitSearch2(jsonPostosList[0]);
            await submitSearch3(jsonPostosList[0]);
            await buscarComparativoMes();

        } catch (error) {
            console.error("Erro ao inicializar dados:", error);
        }
    };

    useEffect(() => {
        setCurrentPage(window.location.pathname);
        handleInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const graficontainerG1Ref = React.useRef<HTMLDivElement>(null);
    const graficontainerG2Ref = React.useRef<HTMLDivElement>(null);
    const graficontainerG3Ref = React.useRef<HTMLDivElement>(null);
    const graficontainerComparativoMesRef = React.useRef<HTMLDivElement>(null);

    const baixarImagem = async (ref: React.RefObject<HTMLDivElement>, nome: string) => {
        if (ref.current) {
            const chart = ref.current.querySelector('.recharts-wrapper');
            const table = ref.current.querySelector('table');

            if (!chart && !table) return;

            // Cria um container temporário com layout em coluna
            const tempDiv = document.createElement('div');
            tempDiv.style.display = 'flex';
            tempDiv.style.flexDirection = 'column';
            tempDiv.style.alignItems = 'stretch';
            tempDiv.style.background = 'white';
            tempDiv.style.padding = '24px';
            tempDiv.style.width = 'max-content'; // <-- Garante largura máxima do conteúdo

            // Gráfico ocupa 100%
            if (chart) {
                const chartWrapper = document.createElement('div');
                chartWrapper.style.width = '100%';
                chartWrapper.style.display = 'flex';
                chartWrapper.style.justifyContent = 'center';
                chartWrapper.appendChild(chart.cloneNode(true));
                tempDiv.appendChild(chartWrapper);
            }

            // Espaço entre gráfico e tabela
            if (chart && table) {
                const spacer = document.createElement('div');
                spacer.style.height = '32px';
                tempDiv.appendChild(spacer);
            }

            // Tabela ocupa 100% (sem overflow)
            if (table) {
                const tableWrapper = document.createElement('div');
                tableWrapper.style.width = 'max-content'; // <-- Garante largura máxima do conteúdo
                tableWrapper.appendChild(table.cloneNode(true));
                tempDiv.appendChild(tableWrapper);
            }

            document.body.appendChild(tempDiv);

            // Aguarda renderização
            await new Promise(resolve => setTimeout(resolve, 100));

            const now = new Date();
            const pad = (n: number) => n.toString().padStart(2, '0');
            const dataHora = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

            // Captura a imagem
            const canvas = await html2canvas(tempDiv, { backgroundColor: '#fff', scale: 2 });

            document.body.removeChild(tempDiv);

            const link = document.createElement('a');
            link.download = `${nome}_${dataHora}.png`;
            link.href = canvas.toDataURL();
            link.click();
        }
    };

    const baixarSomenteGrafico = async (ref: React.RefObject<HTMLDivElement>, nome: string) => {
        if (ref.current) {
            // Seleciona apenas o BarChart dentro do container
            const chart = ref.current.querySelector('.recharts-wrapper');
            if (!chart) return;

            // Cria um div temporário para capturar só o gráfico, sem margem e com largura exata
            const tempDiv = document.createElement('div');
            tempDiv.style.display = 'block';
            tempDiv.style.margin = '0';
            tempDiv.style.padding = '0';
            tempDiv.style.background = 'white';
            tempDiv.style.width = chart.clientWidth + 'px'; // largura exata do gráfico
            tempDiv.style.height = chart.clientHeight + 'px'; // altura exata do gráfico

            // Clona o gráfico e remove qualquer margem automática
            const chartClone = chart.cloneNode(true) as HTMLElement;
            chartClone.style.margin = '0';
            chartClone.style.display = 'block';
            tempDiv.appendChild(chartClone);

            document.body.appendChild(tempDiv);

            // Aguarda renderização
            await new Promise(resolve => setTimeout(resolve, 50));

            const now = new Date();
            const pad = (n: number) => n.toString().padStart(2, '0');
            const dataHora = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

            const canvas = await html2canvas(tempDiv, {
                backgroundColor: '#fff',
                scale: 2,
                width: chart.clientWidth,
                height: chart.clientHeight,
            });

            document.body.removeChild(tempDiv);

            const link = document.createElement('a');
            link.download = `${nome}_grafico_${dataHora}.png`;
            link.href = canvas.toDataURL();
            link.click();
        }
    };

    const exportarTabelaXLSX = (tabela: any[], nome: string) => {
        if (!tabela || tabela.length === 0) return;

        // Monta os dados para exportação
        const header = ['Posto', ...Array.from({ length: 31 }, (_, i) => (i + 1).toString())];
        const data = tabela.map((posto: any) => [
            posto.posto_nome,
            ...Array.from({ length: 31 }, (_, i) => posto[`dia_${String(i + 1).padStart(2, '0')}`] ?? '')
        ]);
        const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Tabela');

        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const dataHora = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

        XLSX.writeFile(wb, `${nome}_tabela_${dataHora}.xlsx`);
    };

    // Função para calcular o maior valor entre as séries do gráfico "Média histórica"
    const getMaxYMediaHistorica = () => {
        if (!dataG3 || dataG3.length === 0) return 100;
        let max = 0;
        dataG3.forEach(item => {
            // Pega todos os valores possíveis
            const valores = [
                Number(item['Ano ' + searchInput.g3_ano]) || 0,
                Number(item['Histórico AESA']) || 0,
                Number(item['Histórico banco de dados']) || 0,
            ];
            const localMax = Math.max(...valores);
            if (localMax > max) max = localMax;
        });
        // Adiciona uma folga de 10%
        return Math.ceil(max * 1.1);
    };

    return (
        <div className="tipo-de-tela">
            <div >
                <div className="card h-100 mb-5">
                    <h5 className="card-header text-center">Postos</h5>
                    <div className="card-body">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th scope="col"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {postosList.map((posto: iPosto, index) => {
                                    return (<tr role='button' key={posto.posto_id} onClick={() => postoClicked(posto)}>
                                        <th scope="row">{index + 1}</th>
                                        <td className='text-nowrap'>{posto.posto_nome}</td>
                                    </tr>);
                                })}
                            </tbody>
                        </table>
                    </div>
                </div >
            </div>

            <div className='recuo-computador overflow-scroll'>

                {/* Gráfico Comparativo de chuvas do mês + tabela */}
                <div className="card mb-5 overflow-hidden">
                    <div className="card-header css-app-cinza-claro d-flex justify-content-between">
                        <div>
                            <h5 className='p-0 m-0'>Comparativo de chuvas do mês</h5>
                            <h5 className='p-0 m-0 pt-2 fw-lighter'>{labelComparativoMes}</h5>
                        </div>
                        <div className='d-flex align-items-center'>
                            <div className="input-group pe-2">
                                <label className={`input-group-text bg-${bootStrapColor}`}>Mês</label>
                                <select className="form-select" value={mesComparativoMes} onChange={e => setMesComparativoMes(e.target.value)}>
                                    <option value="01">Janeiro</option>
                                    <option value="02">Fevereiro</option>
                                    <option value="03">Março</option>
                                    <option value="04">Abril</option>
                                    <option value="05">Maio</option>
                                    <option value="06">Junho</option>
                                    <option value="07">Julho</option>
                                    <option value="08">Agosto</option>
                                    <option value="09">Setembro</option>
                                    <option value="10">Outubro</option>
                                    <option value="11">Novembro</option>
                                    <option value="12">Dezembro</option>
                                </select>
                            </div>
                            <div className="input-group pe-2">
                                <span className={`input-group-text bg-${bootStrapColor}`}>Ano</span>
                                <input type="number" min={0} className="form-control" value={anoComparativoMes} onChange={e => setAnoComparativoMes(Number(e.target.value))} />
                            </div>
                            <button className='btn btn-primary' onClick={buscarComparativoMes}>Buscar</button>
                        </div>
                    </div>
                    <div className="card-body d-flex flex-wrap justify-content-center" id='graficontainer-comparativo-mes' ref={graficontainerComparativoMesRef}>
                        <BarChart
                            height={dataComparativoMes.length !== 0 ? 400 : 0}
                            width={useElementWidth() - 30}
                            data={dataComparativoMes}
                            margin={{
                                top: 60,    // espaço para a Legend
                                right: 30,
                                left: 20,
                                bottom: 100, // aumenta espaço para rótulos rotacionados
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <Legend verticalAlign="top" height={40} />
                            <XAxis
                                dataKey="name"
                                angle={-45}
                                textAnchor="end"
                                interval={0}
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="Chuva total do posto no mês" fill={cores[0] || 'black'}>
                                <LabelList dataKey="Chuva total do posto no mês" position="top" />
                            </Bar>
                        </BarChart>
                        {dataComparativoMes.length === 0 && <h5>Sem registros</h5>}

                        {/* TABELA DE CHUVAS POR DIA */}
                        {tabelaComparativoMes.length > 0 && (
                            <div
                                className="table-responsive mt-4"
                                style={{ overflowX: 'auto' }} // Apenas scroll horizontal, sem maxHeight
                            >
                                <table className="table table-bordered table-striped table-hover">
                                    <thead>
                                        <tr>
                                            <th>Posto</th>
                                            {[...Array(31)].map((_, i) => (
                                                <th key={i + 1}>{i + 1}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="table-group-divider">
                                        {tabelaComparativoMes.map((posto: any, idx: number) => (
                                            <tr key={idx}>
                                                <td className='text-nowrap'>{posto.posto_nome}</td>
                                                {[...Array(31)].map((_, i) => {
                                                    const valor = posto[`dia_${String(i + 1).padStart(2, '0')}`];
                                                    return (
                                                        <td key={i + 1}>
                                                            {valor !== undefined && valor !== null && valor !== ''
                                                                ? Number(valor).toFixed(1)
                                                                : ''}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="d-flex justify-content-end p-2 gap-2">
                        <button className="btn btn-outline-secondary" disabled={dataComparativoMes.length === 0} onClick={() => baixarImagem(graficontainerComparativoMesRef, 'grafico-comparativo-mes')}>Baixar gráfico + tabela</button>
                        <button className="btn btn-outline-secondary" disabled={dataComparativoMes.length === 0} onClick={() => baixarSomenteGrafico(graficontainerComparativoMesRef, 'grafico-comparativo-mes')}>Baixar só o gráfico</button>
                        <button className="btn btn-outline-secondary" disabled={tabelaComparativoMes.length === 0} onClick={() => exportarTabelaXLSX(tabelaComparativoMes, 'grafico-comparativo-mes')}>Exportar tabela XLSX</button>
                    </div>
                </div >

                {/* Gráfico Média histórica */}
                <div className="card mb-5 overflow-hidden" ref={mediaHistoricaRef}>
                    <div className="computador-view card-header css-app-cinza-claro d-flex justify-content-between">
                        <div className="">
                            <h5 className='p-0 m-0'>Média histórica</h5>
                            <h5 className='p-0 m-0 pt-2 fw-lighter'>{labelG3}</h5>
                        </div>
                        <div className='d-flex align-items-center'>
                            <div className="input-group pe-2">
                                <label className={`input-group-text bg-${bootStrapColor}`} htmlFor="inputGroupSelectPosto">Posto</label>
                                <select className="form-select" id="inputGroupSelectPosto" name='g3_posto' value={g3_posto} onChange={element => handleSearchInputChanges(element)}>
                                    <option value=""></option>
                                    {postosList.map((posto: iPosto) => <option key={posto.posto_id} value={posto.posto_id}>{posto.posto_nome}</option>)}
                                </select>
                            </div>
                            <div className="input-group pe-2">
                                <span className={`input-group-text bg-${bootStrapColor}`} id="inputGroup-ano">Anox</span>
                                <input type="number" min={0} className="form-control" aria-label="ano" aria-describedby="inputGroup-ano" name='g3_ano' value={g3_ano} onChange={element => handleSearchInputChanges(element)} />
                            </div>
                            <button className='btn btn-primary' disabled={!searchInput.g3_posto || !searchInput.g3_ano || Number(searchInput.g3_ano) <= 0} onClick={() => submitSearch3()}>Buscar</button>
                        </div>
                    </div>
                    <div className="celular-view card-header css-app-cinza-claro d-flex justify-content-between">
                        <div className="">
                            <h5 className='p-0 m-0'>Média histórica</h5>
                            <div className='d-flex align-items-center mt-3 mb-3'>
                                <div className="input-group pe-2">
                                    <label className={`input-group-text bg-${bootStrapColor}`} htmlFor="inputGroupSelectPosto">Posto</label>
                                    <select className="form-select" id="inputGroupSelectPosto" name='g3_posto' value={g3_posto} onChange={element => handleSearchInputChanges(element)}>
                                        <option value=""></option>
                                        {postosList.map((posto: iPosto) => <option key={posto.posto_id} value={posto.posto_id}>{posto.posto_nome}</option>)}
                                    </select>
                                </div>
                                <div className="input-group pe-2">
                                    <span className={`input-group-text bg-${bootStrapColor}`} id="inputGroup-ano">Ano</span>
                                    <input type="number" min={0} className="form-control" aria-label="ano" aria-describedby="inputGroup-ano" name='g3_ano' value={g3_ano} onChange={element => handleSearchInputChanges(element)} />
                                </div>
                                <button className='btn btn-primary' disabled={!searchInput.g3_posto || !searchInput.g3_ano || Number(searchInput.g3_ano) <= 0} onClick={() => submitSearch3()}>Buscar</button>
                            </div>
                            <h5 className='p-0 m-0 pt-2 fw-lighter'>{labelG3}</h5>
                        </div>
                    </div>
                    <div className="card-body d-flex flex-wrap justify-content-center" ref={graficontainerG3Ref}>
                        <ComposedChart
                            height={dataG3.length !== 0 ? 400 : 0}
                            width={useElementWidth() - 30}
                            data={dataG3}
                            margin={{
                                top: 30,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis domain={[0, getMaxYMediaHistorica()]} /> {/* <-- ajuste aqui */}
                            <Tooltip />
                            <Legend />
                            <Bar dataKey={'Ano ' + searchInput.g3_ano} fill={cores[0] || 'black'} >
                                <LabelList dataKey={'Ano X' + searchInput.g3_ano} position="top" />
                            </Bar>
                            <Line type="monotone" dataKey="Histórico AESA" stroke="red" >
                                <LabelList dataKey={'Histórico AESA'} position="top" />
                            </Line>
                            <Line type="monotone" dataKey="Histórico banco de dados" stroke="blue" >
                                <LabelList dataKey={'Histórico banco de dados'} position="top" />
                            </Line>
                        </ComposedChart>
                        {dataG3.length === 0 && <h5>Sem registros</h5>}
                    </div >
                    <div className="d-flex justify-content-end p-2">
                        <button className="btn btn-outline-secondary" disabled={dataG3.length === 0} onClick={() => baixarImagem(graficontainerG3Ref, 'grafico-media-historica')}>Baixar imagem</button>
                    </div>
                </div >

                {/* Gráfico Comparativo pluviométrico de postos */}
                <div className="card mb-5 overflow-hidden">
                    <div className="computador-view card-header css-app-cinza-claro d-flex justify-content-between">
                        <div className="">
                            <h5 className='p-0 m-0'>Comparativo pluviométrico de postos</h5>
                            <h5 className='p-0 m-0 pt-2 fw-lighter'>Ano {labelG1}</h5>
                        </div>
                        <div className='d-flex align-items-center'>
                            <div className="input-group pe-2">
                                <span className={`input-group-text bg-${bootStrapColor}`} id="inputGroup-ano">Ano</span>
                                <input type="number" min={0} className="form-control" aria-label="ano" aria-describedby="inputGroup-ano" name='g1_ano' value={g1_ano} onChange={element => handleSearchInputChanges(element)} />
                            </div>
                            <button className='btn btn-primary' disabled={!searchInput.g1_ano || Number(searchInput.g1_ano) <= 0} onClick={submitSearch1}>Buscar</button>
                        </div>
                    </div>

                    <div className="celular-view card-header css-app-cinza-claro d-flex justify-content-between">
                        <div className="">
                            <h5 className='p-0 m-0'>Comparativo pluviométrico de postos</h5>
                            <div className='d-flex align-items-center mt-3 mb-3'>
                                <div className="input-group pe-2">
                                    <span className={`input-group-text bg-${bootStrapColor}`} id="inputGroup-ano">Ano</span>
                                    <input type="number" min={0} className="form-control" aria-label="ano" aria-describedby="inputGroup-ano" name='g1_ano' value={g1_ano} onChange={element => handleSearchInputChanges(element)} />
                                </div>
                                <button className='btn btn-primary' disabled={!searchInput.g1_ano || Number(searchInput.g1_ano) <= 0} onClick={submitSearch1}>Buscar</button>
                            </div>
                            <h5 className='p-0 m-0 pt-2 fw-lighter'>Ano {searchInput.g1_ano}</h5>
                        </div>
                    </div>

                    <div className="card-body d-flex flex-wrap justify-content-center" id='graficontainer' ref={graficontainerG1Ref}>

                        <BarChart
                            height={dataG1.length !== 0 ? 400 : 0}
                            width={useElementWidth() - 30}
                            data={dataG1}
                            margin={{
                                top: 30,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            {postosList.map((element, index) => <Bar key={postosList[index].posto_id} dataKey={postosList[index].posto_nome} fill={cores[index] || 'black'}><LabelList dataKey={element.posto_nome} position="top" /></Bar>)}
                        </BarChart>
                        {dataG1.length === 0 && <h5>Sem registros</h5>}
                    </div >
                    <div className="d-flex justify-content-end p-2">
                        <button className="btn btn-outline-secondary" disabled={dataG1.length === 0} onClick={() => baixarImagem(graficontainerG1Ref, 'grafico-comparativo-postos')}>Baixar imagem</button>
                    </div>
                </div >

                {/* Gráfico Pluviometria por dia */}
                <div className="card overflow-hidden">
                    <div className="computador-view card-header css-app-cinza-claro d-flex justify-content-between">
                        <div className="">
                            <h5 className='p-0 m-0'>Pluviometria por dia</h5>
                            <h5 className='p-0 m-0 pt-2 fw-lighter'>{labelG2}</h5>
                        </div>
                        <div className='d-flex align-items-center'>
                            <div className="input-group pe-2">
                                <label className={`input-group-text bg-${bootStrapColor}`} htmlFor="inputGroupSelectPosto">Posto</label>
                                <select className="form-select" id="inputGroupSelectPosto" name='g2_posto' value={g2_posto} onChange={(element) => handleSearchInputChanges(element)}>
                                    <option value=""></option>
                                    {postosList.map((posto: iPosto) => <option key={posto.posto_id} value={posto.posto_id}>{posto.posto_nome}</option>)}
                                </select>
                            </div>
                            <div className="input-group ps-2 pe-2 ">
                                <label className={`input-group-text bg-${bootStrapColor}`} htmlFor="inputGroupSelectMes">Mês</label>
                                <select className="form-select" id="inputGroupSelectMes" name='g2_mes' value={g2_mes} onChange={element => handleSearchInputChanges(element)}>
                                    <option value=""></option>
                                    <option value="01">Janeiro</option>
                                    <option value="02">Fevereiro</option>
                                    <option value="03">Março</option>
                                    <option value="04">Abril</option>
                                    <option value="05">Maio</option>
                                    <option value="06">Junho</option>
                                    <option value="07">Julho</option>
                                    <option value="08">Agosto</option>
                                    <option value="09">Setembro</option>
                                    <option value="10">Outubro</option>
                                    <option value="11">Novembro</option>
                                    <option value="12">Dezembro</option>
                                </select>
                            </div>
                            <div className="input-group pe-2">
                                <span className={`input-group-text bg-${bootStrapColor}`} id="inputGroup-ano">Ano</span>
                                <input type="number" min={0} className="form-control" aria-label="ano" aria-describedby="inputGroup-ano" name='g2_ano' value={g2_ano} onChange={element => handleSearchInputChanges(element)} />
                            </div>
                            <button className='btn btn-primary' disabled={!searchInput.g2_posto || !searchInput.g2_ano || Number(searchInput.g2_ano) <= 0 || !searchInput.g2_mes} onClick={() => submitSearch2()}>Buscar</button>
                        </div>
                    </div>
                    <div className="celular-view card-header css-app-cinza-claro d-flex justify-content-between">
                        <div className="">
                            <h5 className='p-0 m-0'>Pluviometria por dia</h5>
                            <div className='d-flex align-items-center mt-3 mb-3'>
                                <div className="input-group pe-2">
                                    <label className={`input-group-text bg-${bootStrapColor}`} htmlFor="inputGroupSelectPosto">Posto</label>
                                    <select className="form-select" id="inputGroupSelectPosto" name='g2_posto' value={g2_posto} onChange={(element) => handleSearchInputChanges(element)}>
                                        <option value=""></option>
                                        {postosList.map((posto: iPosto) => <option key={posto.posto_id} value={posto.posto_id}>{posto.posto_nome}</option>)}
                                    </select>
                                </div>
                                <div className="input-group ps-2 pe-2 ">
                                    <label className={`input-group-text bg-${bootStrapColor}`} htmlFor="inputGroupSelectMes">Mês</label>
                                    <select className="form-select" id="inputGroupSelectMes" name='g2_mes' value={g2_mes} onChange={element => handleSearchInputChanges(element)}>
                                        <option value=""></option>
                                        <option value="01">Janeiro</option>
                                        <option value="02">Fevereiro</option>
                                        <option value="03">Março</option>
                                        <option value="04">Abril</option>
                                        <option value="05">Maio</option>
                                        <option value="06">Junho</option>
                                        <option value="07">Julho</option>
                                        <option value="08">Agosto</option>
                                        <option value="09">Setembro</option>
                                        <option value="10">Outubro</option>
                                        <option value="11">Novembro</option>
                                        <option value="12">Dezembro</option>
                                    </select>
                                </div>
                                <div className="input-group pe-2">
                                    <span className={`input-group-text bg-${bootStrapColor}`} id="inputGroup-ano">Ano</span>
                                    <input type="number" min={0} className="form-control" aria-label="ano" aria-describedby="inputGroup-ano" name='g2_ano' value={g2_ano} onChange={element => handleSearchInputChanges(element)} />
                                </div>
                                <button className='btn btn-primary' disabled={!searchInput.g2_posto || !searchInput.g2_ano || Number(searchInput.g2_ano) <= 0 || !searchInput.g2_mes} onClick={() => submitSearch2()}>Baixar imagem</button>
                            </div>
                            <h5 className='p-0 m-0 pt-2 fw-lighter'>{labelG2}</h5>
                        </div>
                    </div>

                    <div className="card-body d-flex flex-wrap justify-content-center" ref={graficontainerG2Ref}>
                        <BarChart
                            height={dataG2.length !== 0 ? 400 : 0}
                            width={useElementWidth() - 30}
                            data={dataG2}
                            margin={{
                                top: 30,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey={'Chuva'} fill={cores[0] || 'black'} >
                                <LabelList dataKey={'Chuva'} position="top" />
                            </Bar>
                            <Bar dataKey={'Acumulado'} fill={cores[2] || 'black'} >
                                <LabelList dataKey={'Acumulado'} position="top" />
                            </Bar>
                        </BarChart>
                        {dataG2.length === 0 && <h5>Sem registros</h5>}
                    </div >
                    <div className="d-flex justify-content-end p-2">
                        <button className="btn btn-outline-secondary" disabled={dataG2.length === 0} onClick={() => baixarImagem(graficontainerG2Ref, 'grafico-pluviometria-dia')}>Baixar imagem</button>
                    </div>
                </div >

            </div >
        </div >
    );
};

export default Graficos;
